import dayjs from 'dayjs'

export const display = (value, emptyText = '-') => (
  value === null || value === undefined || value === '' ? emptyText : value
)

export const unwrapList = value => (
  Array.isArray(value) ? value : ((value && (value.list || value.data)) || [])
)

export const settleRequest = promise => promise.then(
  value => ({ ok: true, value }),
  error => ({ ok: false, error })
)

export const buildDatasetTableOptions = detail => {
  const tables = detail && Array.isArray(detail.tables) ? detail.tables : []
  if (tables.length > 0) {
    return tables
      .filter(table => table.tableId !== null && table.tableId !== undefined)
      .map(table => ({
        value: table.tableId,
        label: table.displayName
          ? `${table.displayName}${table.logicalName ? `（${table.logicalName}）` : ''}`
          : (table.logicalName || `逻辑表 #${table.tableId}`)
      }))
  }

  const fields = detail && Array.isArray(detail.fields) ? detail.fields : []
  const tableIds = []
  fields.forEach(field => {
    if (field.tableId !== null && field.tableId !== undefined && !tableIds.includes(field.tableId)) {
      tableIds.push(field.tableId)
    }
  })
  return tableIds.map(tableId => ({ value: tableId, label: `逻辑表 #${tableId}` }))
}

export const formatDateTime = (value, emptyText = '从未上传过数据') => {
  if (!value) return emptyText
  const date = dayjs(value)
  return date.isValid() ? date.format('YYYY-MM-DD HH:mm:ss') : display(value, emptyText)
}

/** 列表用：只展示到日，不展示时分秒。 */
export const formatDate = (value, emptyText = '从未上传过数据') => {
  if (!value) return emptyText
  const date = dayjs(value)
  return date.isValid() ? date.format('YYYY-MM-DD') : display(value, emptyText)
}

export const formatHealthyRate = value => {
  if (value === null || value === undefined || value === '') return null
  const number = Number(value)
  if (!Number.isFinite(number)) return String(value)
  // 接口返回比例值（0~1）；兼容历史上直接返回百分数的接口。
  return `${(number <= 1 ? number * 100 : number).toFixed(2)}%`
}

export const healthyRateSummary = value => {
  const rate = formatHealthyRate(value)
  return rate ? `${rate} 健康率` : '暂无可计算数据源'
}

export const formatInteger = (value, emptyText = '-') => {
  if (value === null || value === undefined || value === '') return emptyText
  const number = Number(value)
  if (!Number.isFinite(number)) return display(value, emptyText)
  return number.toLocaleString('zh-CN')
}

export const formatUpdateFrequency = value => ({
  daily: '每日',
  weekly: '每周',
  monthly: '每月',
  quarterly: '每季度',
  irregular: '不定期'
}[value] || display(value))

export const getHealthStatus = dataset => dataset && dataset.healthStatus
  ? dataset.healthStatus
  : (dataset && dataset.projectKind === 'application' ? null : 'unknown')

export const normalizeOverviewSummary = (summary = {}, datasets = []) => {
  const rows = Array.isArray(datasets) ? datasets.filter(item => item.projectKind !== 'application') : []
  const fallbackHealthy = rows.filter(item => getHealthStatus(item) === 'healthy').length
  const fallbackWarning = rows.filter(item => getHealthStatus(item) === 'warning').length
  const datasetCount = Number(summary.datasetCount ?? rows.length) || 0
  const healthyCount = Number(summary.healthyCount ?? fallbackHealthy) || 0
  const warningCount = Number(summary.warningCount ?? fallbackWarning) || 0
  const calculableDatasetCount = Number(summary.calculableDatasetCount ?? (healthyCount + warningCount)) || 0
  const unknownCount = Number(summary.unknownCount ?? Math.max(datasetCount - calculableDatasetCount, 0)) || 0
  const unconfiguredRuleDatasetCount = summary.unconfiguredRuleDatasetCount === null || summary.unconfiguredRuleDatasetCount === undefined
    ? null
    : (Number(summary.unconfiguredRuleDatasetCount) || 0)
  return { datasetCount, healthyCount, warningCount, unknownCount, unconfiguredRuleDatasetCount }
}

export const healthMeta = value => ({
  healthy: { status: 'success', label: '按时' },
  warning: { status: 'warning', label: '超期' },
  failing: { status: 'error', label: '故障' },
  unknown: { status: 'default', label: '未设置周期' }
}[value] || { status: 'default', label: '未知' })

export const ruleStatusMeta = value => ({
  draft: { color: 'default', label: '草稿' },
  confirmed: { color: 'blue', label: '已确认' },
  enabled: { color: 'green', label: '已启用' },
  disabled: { color: 'orange', label: '已停用' },
  archived: { color: 'default', label: '已归档' }
}[value] || { color: 'default', label: display(value, '未知') })

const UNSAVABLE_CANDIDATE_STATUSES = ['failed', 'error', 'invalid', 'unsafe', 'rejected']

export const isCandidateSavable = candidate => {
  if (!candidate || typeof candidate.generatedSql !== 'string' || !candidate.generatedSql.trim()) return false
  const status = String(candidate.status || '').toLowerCase()
  return !UNSAVABLE_CANDIDATE_STATUSES.includes(status)
}

export const isQualityCandidateSavable = candidate => (
  isCandidateSavable(candidate) &&
  candidate.tableId !== null && candidate.tableId !== undefined &&
  typeof candidate.ruleName === 'string' && Boolean(candidate.ruleName.trim()) &&
  typeof candidate.normalizedRuleJson === 'string' && Boolean(candidate.normalizedRuleJson.trim()) &&
  Array.isArray(candidate.referencedFields) && candidate.referencedFields.length > 0
)

export const isScheduleCandidateSavable = candidate => {
  if (!candidate || typeof candidate.normalizedRuleJson !== 'string' || !candidate.normalizedRuleJson.trim()) return false
  const status = String(candidate.status || '').toLowerCase()
  const preview = candidate.schedulePreview
  const items = preview && Array.isArray(preview.items) ? preview.items : []
  return !UNSAVABLE_CANDIDATE_STATUSES.includes(status) &&
    Number(preview.requestedCount) === 50 && items.length === 50
}

export const candidateUnavailableMessage = (candidate, fallback) => {
  if (candidate && typeof candidate.explanation === 'string' && candidate.explanation.trim()) {
    return candidate.explanation.trim()
  }
  const ambiguities = candidate && Array.isArray(candidate.ambiguities) ? candidate.ambiguities : []
  if (ambiguities.length > 0 && String(ambiguities[0]).trim()) return String(ambiguities[0]).trim()
  const risks = candidate && Array.isArray(candidate.risks) ? candidate.risks : []
  if (risks.length > 0 && String(risks[0]).trim()) return String(risks[0]).trim()
  return fallback
}

export const formatSchedulePeriod = value => ({
  daily: '每日',
  weekly: '每周',
  monthly: '每月',
  irregular: '不定期'
}[value] || display(value))

export const scheduleStatusMeta = value => ({
  pending: { color: 'blue', label: '待上传' },
  satisfied: { color: 'green', label: '已完成' },
  overdue: { color: 'orange', label: '已逾期' },
  cancelled: { color: 'default', label: '已取消' }
}[value] || { color: 'default', label: display(value, '未知') })
